import React from 'react';
import styled from '@emotion/styled';
import { fromTheme } from '@kyruus/ui-theme';
import { Tabs } from '@biome-design-system/ui/Tabs';
import { Tab } from '@biome-design-system/ui/Tab';

// Tab labels are links, so they take the theme's link colors rather than MUI's
// unselected text color
const Nav = styled.nav`
  & .MuiTab-root {
    color: ${fromTheme('color_link')};
  }

  & .MuiTab-root:hover,
  & .MuiTab-root:focus-visible {
    color: ${fromTheme('color_link_hover')};
  }
`;

export interface SectionNavTab {
  id: string;
  label: React.ReactNode;
  href?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  name?: string; // analytics metadata
  disabled?: boolean;
}

export interface SectionNavTabsProps {
  id: string; // 'location-nav' | 'profile-nav'
  'aria-label': string;
  tabs: SectionNavTab[];
  getTabControlId: (tabId: string) => string;
  onActivate?: (
    event: React.MouseEvent<HTMLAnchorElement>,
    id: string,
    tab: SectionNavTab
  ) => void;
}

export function SectionNavTabs(props: SectionNavTabsProps): React.ReactElement {
  const {
    id,
    'aria-label': ariaLabel,
    tabs,
    getTabControlId,
    onActivate
  } = props;

  return (
    <Nav id={id} aria-label={ariaLabel}>
      <Tabs
        value={false}
        variant="scrollable"
        scrollButtons="auto"
        // Override MUI tablist semantics for navigation mode
        slotProps={{
          list: {
            // Remove role="tablist" - let it be a regular container
            role: undefined
          }
        }}
      >
        {tabs.map((tab) => {
          const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            // Disabled tabs neither navigate nor report analytics
            if (tab.disabled) {
              event.preventDefault();
              return;
            }

            if (onActivate) {
              onActivate(
                event as React.MouseEvent<HTMLAnchorElement>,
                tab.id,
                tab
              );
            }
          };

          return (
            <Tab
              key={tab.id}
              component="a"
              href={tab.href}
              id={getTabControlId(tab.id)}
              aria-label={tab.ariaLabel}
              aria-labelledby={tab.ariaLabelledBy}
              // Don't use disabled prop - it forces tabIndex=-1 in ButtonBase
              // Use aria-disabled instead and handle styling via sx
              aria-disabled={tab.disabled || undefined}
              onClick={handleClick}
              label={tab.label}
              // Override MUI tab semantics: MUI spreads {...other} after its own
              // role/aria-selected/tabIndex, so these override them
              role={undefined} // Remove role="tab", keep native <a> role
              aria-selected={undefined} // Remove aria-selected from navigation links
              // Force natural tab order (MUI sets -1 on unselected tabs); disabled
              // links are removed from the tab order
              tabIndex={tab.disabled ? -1 : 0}
              // Apply disabled styling without using disabled prop
              sx={
                tab.disabled
                  ? {
                      opacity: 0.38,
                      pointerEvents: 'none'
                    }
                  : undefined
              }
            />
          );
        })}
      </Tabs>
    </Nav>
  );
}
